import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResumoTab } from '@/components/dashboard/tabs/ResumoTab';
import { RecuperacaoTab } from '@/components/dashboard/tabs/RecuperacaoTab';
import { ReguaTab } from '@/components/dashboard/tabs/ReguaTab';
import { DevedoresTab } from '@/components/dashboard/tabs/DevedoresTab';
import { PDFExportButton } from '@/components/dashboard/PDFExportButton';
import {
  DATA_REFERENCIA,
  getKpisResumo,
  getKpisRecuperacao,
  getKpisDevedores,
  getTopDevedores,
  getStepsStats,
  getChartData,
  getAgingOpen,
  getAgingOverdue,
} from '@/data/dashboardMockData';

// ============= MODO DEMO =============
const USE_MOCK_DATA = true;

type TimeFilter = 'day' | 'week' | 'month';

const Dashboard = () => {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('week');

  // Fetch real data from Supabase (only when USE_MOCK_DATA = false)
  const { isLoading } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id, full_name')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) return null;

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const { data: overdueInvoices } = await supabase
        .from('invoices')
        .select('amount')
        .eq('company_id', profile.company_id)
        .eq('status', 'overdue');

      const { data: paidInvoices } = await supabase
        .from('invoices')
        .select('amount')
        .eq('company_id', profile.company_id)
        .eq('status', 'paid')
        .gte('paid_at', startOfMonth.toISOString())
        .lte('paid_at', endOfMonth.toISOString());

      const atraso = overdueInvoices?.reduce((sum, inv) => sum + Number(inv.amount), 0) || 0;
      const recebido = paidInvoices?.reduce((sum, inv) => sum + Number(inv.amount), 0) || 0;

      return { atraso, recebido };
    },
    enabled: !USE_MOCK_DATA
  });

  const handleContactChannel = (debtor: { id: string; name: string }, channel: 'email' | 'sms' | 'whatsapp') => {
    console.log(`Contacting ${debtor.name} via ${channel}`);
    // TODO: Implementar ação de contato
  };

  if (isLoading && !USE_MOCK_DATA) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com Título "Indicadores" e Botão PDF */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Indicadores</h1>
          <p className="text-sm text-muted-foreground">
            Painel de controle e métricas de cobrança
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Dados atualizados em: {DATA_REFERENCIA}
          </p>
        </div>
        <PDFExportButton />
      </div>

      {/* Tabs de Navegação */}
      <Tabs defaultValue="resumo" className="w-full">
        <TabsList className="w-full justify-start bg-transparent border-b border-border rounded-none h-auto p-0 gap-0">
          <TabsTrigger 
            value="resumo"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[hsl(var(--basic-500))] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm font-medium"
          >
            Resumo
          </TabsTrigger>
          <TabsTrigger 
            value="recuperacao"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[hsl(var(--basic-500))] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm font-medium"
          >
            Recuperação
          </TabsTrigger>
          <TabsTrigger 
            value="regua"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[hsl(var(--basic-500))] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm font-medium"
          >
            Régua de Cobrança
          </TabsTrigger>
          <TabsTrigger 
            value="devedores"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[hsl(var(--basic-500))] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm font-medium"
          >
            Devedores
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="resumo" className="m-0">
            <ResumoTab 
              kpiData={getKpisResumo()}
              chartData={getChartData(timeFilter)}
              agingData={getAgingOpen()}
              timeFilter={timeFilter}
              onTimeFilterChange={setTimeFilter}
            />
          </TabsContent>

          <TabsContent value="recuperacao" className="m-0">
            <RecuperacaoTab kpiData={getKpisRecuperacao()} />
          </TabsContent>

          <TabsContent value="regua" className="m-0">
            <ReguaTab stepsStats={getStepsStats()} />
          </TabsContent>

          <TabsContent value="devedores" className="m-0">
            <DevedoresTab 
              kpiData={getKpisDevedores()}
              debtors={getTopDevedores()}
              agingData={getAgingOverdue()}
              onContactChannel={handleContactChannel}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default Dashboard;
